import {HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse} from '@angular/common/http';
import {inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {switchMap, catchError, map, retry} from 'rxjs/operators';
import {ENV} from '../environment';
import { ToastService } from './toast.service';

let csrfToken: string | null = null;

export const csrfInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const http = inject(HttpClient);
  const toast = inject(ToastService);

  // Skip CSRF token for the token endpoint itself to avoid infinite loop
  if (req.url.includes('csrf-token')) {
    return next(req);
  }

  if (req.method === 'POST' || req.method === 'PUT' || req.method === 'DELETE') {
    if (csrfToken) {
      return handleRequestWithToken(req, next, csrfToken, http).pipe(
        catchError(error => {
          // If we get 403 (Forbidden), try to refresh CSRF token and retry
          if (error instanceof HttpErrorResponse && error.status === 403) {
            csrfToken = null; // Clear cached token
            return fetchCsrfToken(http).pipe(
              switchMap(token => {
                csrfToken = token;
                return handleRequestWithToken(req, next, token, http);
              }),
              catchError(fetchError => {
                console.error('Failed to refresh CSRF token after 403', fetchError);
                return throwError(() => error);
              })
            );
          }
          return throwError(() => error);
        })
      );
    } else {
      return fetchCsrfToken(http).pipe(
        switchMap(token => {
          csrfToken = token;
          return handleRequestWithToken(req, next, token, http);
        }),
        catchError(error => {
          console.error('Failed to fetch CSRF token', error);
          return throwError(() => error);
        })
      );
    }
  }
  return next(req).pipe(
    catchError((err: any) => {
      if (err instanceof HttpErrorResponse && err.error && typeof err.error === 'object') {
        const maybeDto = err.error as any;
        if ('code' in maybeDto && 'message' in maybeDto) {
          toast.error(maybeDto.message || 'Request failed');
          return throwError(() => ({ httpStatus: err.status, code: maybeDto.code, message: maybeDto.message }));
        }
      }
      return throwError(() => err);
    })
  );
};

function fetchCsrfToken(http: HttpClient): Observable<string> {
  return http.get<any>(ENV.baseUrl + 'csrf-token', {
    withCredentials: true
  }).pipe(
    map(response => response.token || ''),
    retry(2), // Retry up to 2 times if request fails
    catchError(error => {
      console.error('CSRF token fetch error:', error);
      return throwError(() => error);
    })
  );
}

function handleRequestWithToken(req: HttpRequest<any>, next: HttpHandlerFn, token: string, http: HttpClient): Observable<HttpEvent<any>> {
  const clonedReq = req.clone({
    headers: req.headers.set('X-CSRF-TOKEN', token),
    withCredentials: true
  });
  return next(clonedReq);
}
